import React, { useState, useEffect } from 'react';
import { Mail, Loader2, Check, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import api from '../lib/api';
import { trackEvent } from '../hooks/useAnalytics';
import { toast } from '../hooks/use-toast';

/**
 * Persistent sticky bottom email-capture banner.
 *
 * Renders once the visitor has generated a preview ad on the landing page
 * but hasn't yet dropped their email via either the hero form or the exit
 * intent modal. It follows them as they scroll, providing a permanent,
 * low-friction way to capture the lead.
 *
 * Dismissable for the session (sessionStorage flag) so we don't get
 * cookie-banner fatigue, but it'll come back next session if they didn't
 * convert.
 */
const StickyEmailCapture = ({ preview, bizName = '' }) => {
  const [dismissed, setDismissed] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [visible, setVisible] = useState(false);

  // Fade in 1.5s after preview lands so it doesn't compete with the hero
  // CTA visually — visitor has time to read the headline first.
  useEffect(() => {
    if (!preview) return;
    try {
      if (sessionStorage.getItem('mbap_sticky_dismissed')) {
        // Defer setDismissed off the synchronous effect path so we don't
        // trip react-hooks/set-state-in-effect.
        Promise.resolve().then(() => setDismissed(true));
        return;
      }
    } catch {/* ignore */}
    const t = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(t);
  }, [preview]);

  const dismiss = () => {
    try { sessionStorage.setItem('mbap_sticky_dismissed', '1'); } catch {/* ignore */}
    setDismissed(true);
    trackEvent('sticky_capture_dismissed', { hasPreview: !!preview });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !email.includes('@')) {
      toast({ title: 'Enter a valid email' });
      return;
    }
    setSending(true);
    trackEvent('sticky_capture_submit', { hasPreview: !!preview });
    try {
      await api.post('/auth/magic-link', {
        email: email.trim().toLowerCase(),
        name: bizName?.trim() || '',
        previewId: preview?.previewId || '',
        source: 'sticky_capture',
      });
      setSent(true);
      trackEvent('sticky_capture_captured', { hasPreview: !!preview });
    } catch (err) {
      toast({ title: 'Could not send link', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setSending(false);
    }
  };

  if (!preview || dismissed || !visible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-500 ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
      data-testid="sticky-email-capture"
    >
      <div className="bg-gradient-to-r from-orange-500 to-rose-500 shadow-2xl">
        <div className="max-w-5xl mx-auto px-4 py-3 sm:py-4 flex items-center gap-3 sm:gap-5">
          <div className="hidden sm:flex w-10 h-10 rounded-xl bg-white/20 backdrop-blur items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>

          {!sent ? (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-sm sm:text-base leading-tight">
                  💾 Save this ad as PDF — drop your email
                </div>
                <div className="hidden sm:block text-white/85 text-xs mt-0.5">
                  We&apos;ll send your AI ad + a one-click sign-in. No card. 3 more free ads inside.
                </div>
              </div>
              <form onSubmit={submit} className="flex items-center gap-2 flex-shrink-0" data-testid="sticky-form">
                <div className="relative hidden sm:block">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="h-10 pl-10 pr-3 w-56 rounded-lg border-0 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-white"
                    data-testid="sticky-email-input"
                  />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="sm:hidden h-10 px-3 w-32 rounded-lg border-0 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-white"
                  data-testid="sticky-email-input-mobile"
                />
                <Button
                  type="submit"
                  disabled={sending}
                  className="h-10 px-4 sm:px-5 bg-white hover:bg-slate-100 text-slate-900 font-bold rounded-lg whitespace-nowrap"
                  data-testid="sticky-email-submit"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Save ad <span className="hidden sm:inline ml-1">→</span></>}
                </Button>
              </form>
              <button
                onClick={dismiss}
                aria-label="Dismiss"
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg flex-shrink-0"
                data-testid="sticky-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          ) : (
            <div className="flex-1 flex items-center gap-3 text-white" data-testid="sticky-sent">
              <Check className="w-5 h-5 flex-shrink-0" strokeWidth={3} />
              <div className="text-sm font-bold">Check your inbox — we just sent your ad to {email}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StickyEmailCapture;
